import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./collapsible";

/** Grouped-list scaffolding: a sticky, collapsible group header over a stack of `ListRow`s. */

export interface GroupHeaderProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onToggle"
> {
  /** Group name, e.g. "In Progress". */
  label: string;
  /** Item count, rendered as tertiary tabular meta — never a Badge. */
  count?: number;
  /** CSS colour for the leading dot (board / status colour). */
  color?: string;
  /** Replaces the leading dot entirely. */
  icon?: React.ReactNode;
  /** Show the disclosure chevron and make the header a toggle. Default true. */
  collapsible?: boolean;
  /** Current open state — drives chevron rotation and `aria-expanded`. */
  open?: boolean;
  /** Right-aligned actions, revealed on hover / focus-within. */
  actions?: React.ReactNode;
  /** Set when the header is *not* wrapped by a `CollapsibleTrigger`. */
  onToggle?: () => void;
  /** Stick the header to the top of the scroll container. Default true. */
  sticky?: boolean;
  /**
   * Render the disclosure as a Radix `CollapsibleTrigger`. Only valid inside a
   * `Collapsible` — `ListSection` sets this for you.
   */
  asTrigger?: boolean;
}

const GroupHeader = React.forwardRef<HTMLDivElement, GroupHeaderProps>(
  (
    {
      label,
      count,
      color,
      icon,
      collapsible = true,
      open = true,
      actions,
      onToggle,
      sticky = true,
      asTrigger = false,
      className,
      ...props
    },
    ref,
  ) => {
    const trigger = (
      <>
        {collapsible ? (
          <ChevronDownIcon
            aria-hidden="true"
            data-open={open ? "true" : "false"}
            className={cn(
              "size-3 shrink-0 text-text-tertiary",
              "transition-transform duration-150 ease-out-fast",
              "data-[open=false]:-rotate-90",
            )}
          />
        ) : null}
        {icon ? (
          <span className="flex shrink-0 items-center [&_svg]:size-3.5 [&_svg]:text-text-tertiary">
            {icon}
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full bg-text-tertiary"
            style={color ? { backgroundColor: color } : undefined}
          />
        )}
        <span className="truncate text-xs font-semibold text-text">
          {label}
        </span>
        {count !== undefined ? (
          <span className="text-xs tabular-nums text-text-tertiary">
            {count}
          </span>
        ) : null}
      </>
    );

    const triggerClassName = cn(
      "flex h-full min-w-0 flex-1 items-center gap-2 text-left",
      "rounded-sm focus-visible:outline-none focus-bar",
      collapsible ? "cursor-pointer" : "cursor-default",
    );

    return (
      <div
        ref={ref}
        data-slot="group-header"
        className={cn(
          // Inset band, not a full-bleed rule: the header belongs to the group
          // under it, and a strip that runs to both edges of the scroll
          // container reads as page furniture instead. `mx-4` matches the
          // gutter every list surface already uses, so the band lines up with
          // the rows and tiles it heads.
          "group mx-4 flex h-control-lg select-none items-center gap-2 rounded-md bg-page px-2",
          "transition-[background-color] duration-100 ease-out-fast hover:bg-hover",
          sticky && "sticky top-0 z-10",
          className,
        )}
        {...props}
      >
        {asTrigger ? (
          <CollapsibleTrigger className={triggerClassName} onClick={onToggle}>
            {trigger}
          </CollapsibleTrigger>
        ) : collapsible || onToggle ? (
          <button
            type="button"
            className={triggerClassName}
            aria-expanded={collapsible ? open : undefined}
            onClick={onToggle}
          >
            {trigger}
          </button>
        ) : (
          <div className={triggerClassName}>{trigger}</div>
        )}
        {actions ? (
          <div
            className={cn(
              "ml-auto flex shrink-0 items-center gap-1",
              "opacity-0 transition-opacity duration-100 ease-out-fast",
              "group-hover:opacity-100 focus-within:opacity-100",
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
    );
  },
);
GroupHeader.displayName = "GroupHeader";

export interface ListSectionProps {
  label: string;
  count?: number;
  /** CSS colour for the leading dot. */
  color?: string;
  /** Overrides the dot. */
  icon?: React.ReactNode;
  /** Default true. */
  collapsible?: boolean;
  /** Default true. */
  defaultOpen?: boolean;
  /** Controlled open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Right-aligned header actions, revealed on hover (e.g. an inline "+"). */
  actions?: React.ReactNode;
  /** Stick the header to the top of the scroll container. Default true. */
  sticky?: boolean;
  className?: string;
  /** Header-only class overrides. */
  headerClassName?: string;
  children: React.ReactNode;
}

const ListSection = React.forwardRef<HTMLDivElement, ListSectionProps>(
  (
    {
      label,
      count,
      color,
      icon,
      collapsible = true,
      defaultOpen = true,
      open,
      onOpenChange,
      actions,
      sticky = true,
      className,
      headerClassName,
      children,
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : uncontrolledOpen;

    const handleOpenChange = React.useCallback(
      (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next);
        onOpenChange?.(next);
      },
      [isControlled, onOpenChange],
    );

    return (
      <Collapsible
        ref={ref}
        // Styling hook only — carries no styles here, so every existing
        // consumer renders exactly as before.
        data-slot="list-section"
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={!collapsible}
        className={className}
      >
        <GroupHeader
          label={label}
          count={count}
          color={color}
          icon={icon}
          collapsible={collapsible}
          open={isOpen}
          actions={actions}
          sticky={sticky}
          asTrigger
          className={headerClassName}
        />
        <CollapsibleContent>{children}</CollapsibleContent>
      </Collapsible>
    );
  },
);
ListSection.displayName = "ListSection";

export { ListSection, GroupHeader };
